import React from 'react';
import PropTypes from 'prop-types';

/**
 * Deprecated seit Okt 2026, neue Kennzahlen über ui/StatRow.
 * (Bestehende Aufrufer werden schrittweise umgestellt; bis dahin bleibt die
 * Kachel funktionsgleich.)
 *
 * StatTile — kompakte, mobile-first Kennzahl-Kachel.
 *
 * Der App-Standard für Stat-Anzeigen (Sessions, Lernzeit, Aura, „Heute"-Werte …).
 * Dichte wie im Logbuch: 2-spaltig auf Mobile, kleine Zahl + Label, Icon-Chip.
 * Farbe ausschließlich über die `accent`-Prop (feste Lookup-Map, damit Tailwind-Purge greift).
 *
 * Verwendung im Grid: <div className="grid grid-cols-2 sm:grid-cols-4 gap-2"> … </div>
 */

// Vollständige Klassen-Strings pro Akzent (keine dynamische Interpolation → Tailwind-safe).
//
// `primary` ist die Vorgabe und seit Okt 2026 neutral: Chip-Flaeche, Icon in
// Tinte — so wie die Kennzahlen im Werbespot und auf der Website (Motion-Kit
// `.eo-stat`). Im August waren alle Kacheln violett getoent („die Reihe wirkte
// tot“); im Spot traegt die Gliederung aber Icon, Zahl und Abstand, und vier
// lila Kacheln verbrauchen das ganze Lila-Budget eines Bildschirms
// (CorporateDesign Regel 6: Farbe traegt Bedeutung, nicht Kategorie).
//
// `aura` bleibt getoent: Aura IST die Marke, das ist Bedeutung.
const ACCENTS = {
  primary: { chip: 'bg-chip ring-transparent', icon: 'text-foreground' },
  aura: { chip: 'bg-primary/10 ring-transparent', icon: 'text-primary' },
  blue: { chip: 'bg-info/15 ring-info/10', icon: 'text-info' },
  orange: { chip: 'bg-warning/15 ring-warning/10', icon: 'text-warning' },
  green: { chip: 'bg-success/15 ring-success/10', icon: 'text-success' },
};

// `subtitle` (Sep 2026): zweite, leisere Zeile unter dem Label — für Kennzahlen mit
// Einordnung („abgeschlossen / gesamt", „aus Präsenz-Tracking"). Vorher baute das
// Forschungs-Panel dafür eigene Karten neben dem Baustein.
const StatTile = ({ icon: Icon, value, label, subtitle, accent = 'primary', delay, onClick, ariaLabel }) => {
  const { chip, icon } = ACCENTS[accent] || ACCENTS.primary;
  // Mit `onClick` wird aus der Kachel ein echter Knopf (Dashboard-Uebersichten
  // verlinken von der Kennzahl auf die zugehoerige Liste). Bewusst als <button>
  // statt <div onClick>: Tastaturbedienung und Rolle kommen dann vom Element
  // selbst. Die Hover-/Fokus-Anmutung ist die von `ui/Card` (variant
  // "interactive") — eine Wahrheit statt einer zweiten Interaktions-Optik.
  const Tag = onClick ? 'button' : 'div';
  const interactive = onClick
    ? {
      type: 'button',
      onClick,
      'aria-label': ariaLabel,
    }
    : {};
  return (
    // Die Flaeche stand bis August 2026 als `SharedStyles.glassContainerSecondary` hier —
    // ein Primitive des Design-Systems hing damit ausgerechnet an dem Stylesheet, das es
    // abloesen soll (und das ESLint fuer alle anderen Dateien bereits verbietet).
    // Die Klassen sind unveraendert uebernommen, nur direkt notiert.
    <Tag
      className={[
        'bg-surface-raised rounded-xl shadow-token-sm border border-border',
        'flex items-center gap-2.5 px-3 py-2.5 sm:gap-3 sm:px-4 sm:py-3 animate-fade-in',
        onClick
          ? 'w-full text-left transition-[box-shadow,border-color] duration-base hover:shadow-token-md hover:border-primary/30 ' +
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
          : '',
      ].filter(Boolean).join(' ')}
      style={delay != null ? { animationDelay: `${delay}ms`, animationFillMode: 'backwards' } : undefined}
      {...interactive}
    >
      <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg ring-1 flex items-center justify-center flex-shrink-0 ${chip}`}>
        <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${icon}`} aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <div className="text-sm sm:text-base font-semibold text-foreground leading-tight truncate">{value}</div>
        <div className="text-2xs sm:text-xs text-muted-foreground leading-tight truncate">{label}</div>
        {subtitle && (
          <div className="text-2xs text-faint leading-tight truncate">{subtitle}</div>
        )}
      </div>
    </Tag>
  );
};

StatTile.propTypes = {
  icon: PropTypes.elementType.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
  /** Leisere Einordnung unter dem Label (optional) */
  subtitle: PropTypes.node,
  accent: PropTypes.oneOf(['primary', 'aura', 'blue', 'orange', 'green']),
  delay: PropTypes.number,
  /** Macht die Kachel zum Knopf (z.B. Kennzahl -> zugehoerige Liste). */
  onClick: PropTypes.func,
  /** Zugaenglicher Name des Knopfes, wenn Wert+Label allein nicht sprechen. */
  ariaLabel: PropTypes.string,
};

export default StatTile;
